"use client";

import { useState, useEffect, useRef } from "react";
import { RotateCcw } from "lucide-react";

const Typewriter = ({ text, speed = 40 }: { text: string; speed?: number }) => {
  const [displayedText, setDisplayedText] = useState("");

  useEffect(() => {
    setDisplayedText("");
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayedText((prev) => prev + text.charAt(i));
        i++;
      } else {
        clearInterval(timer);
      }
    }, speed);
    return () => clearInterval(timer);
  }, [text, speed]);

  return <span>{displayedText}</span>;
};

interface HeroSectionProps {
  onBookDemo?: () => void;
  onOpenDemo?: () => void;
}

export default function HeroSection({ onBookDemo, onOpenDemo }: HeroSectionProps) {
  const handleDemo = onBookDemo || onOpenDemo;
  const [timerSeconds, setTimerSeconds] = useState(21);
  const [visibleMessages, setVisibleMessages] = useState<number>(8);
  const [isReplaying, setIsReplaying] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const waveformHeights = [
    12, 16, 20, 14, 24, 18, 10, 16, 22, 14, 8, 12, 18, 26, 20, 14, 22, 16, 12, 18, 24, 16, 10, 14, 20, 14, 22, 18, 12, 16, 24, 14, 10, 16, 12, 8
  ];

  useEffect(() => {
    const playAudio = async () => {
      try {
        if (audioRef.current) {
          await audioRef.current.play();
          setIsReplaying(true);
        }
      } catch (err) {
        console.log("Autoplay blocked by browser. User must click to play.");
      }
    };
    playAudio();
  }, []);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [visibleMessages]);

  const messages = [
    {
      time: 0,
      sender: "Priya (EchoLeads AI)",
      role: "ai",
      text: "Hi, आदित्य. This is प्रिया calling from Sipani Properties. Am I speaking at a good time?",
    },
    {
      time: 6,
      sender: "Aditya, lead",
      role: "lead",
      text: "Yes, tell me.",
    },
    {
      time: 8,
      sender: "Priya (EchoLeads AI)",
      role: "ai",
      text: "Great. You had recently shown interest in our project, Codename Saja Field, Sipani City. So I just wanted to connect and understand your requirement better. Just to give you a quick context. This project is coming up in a very good location. Around 5 minutes from NPS वार्ता with strong connectivity to साजपुर and White Field. 1 small question. Looking at this property for your end use or for investment purpose?",
    },
    {
      time: 33,
      sender: "Aditya, lead",
      role: "lead",
      text: "It's more like an investment purpose.",
    },
    {
      time: 36,
      sender: "Priya (EchoLeads AI)",
      role: "ai",
      text: "Great. It is early phase pricing, and due to upcoming demand in the Sarget belt, it has strong potential for both appreciation and rental. May I know what approximate budget range are you considering?",
    },
    {
      time: 48,
      sender: "Aditya, lead",
      role: "lead",
      text: "Anywhere from I don't know, 1.2 to 1.5 CR.",
    },
    {
      time: 53,
      sender: "Priya (EchoLeads AI)",
      role: "ai",
      text: "Ok, got it. Our 3 BHK apartments start from around rupees 99 lakhs to 1 crore 14 lakhs. Which fits well within your budget range. Honestly, the best way to",
    },
    {
      time: 63,
      sender: "Aditya, lead",
      role: "lead",
      text: "Can you tell me the location of this place?",
    },
    {
      time: 66,
      sender: "Priya (EchoLeads AI)",
      role: "ai",
      text: "Sure, the project is located in माडपाना हाली. It is just sure. The project is located in माडपाना हाली. It is just 5 minutes from NPS Varther and offers connectivity to साजपुर and Whitefield. So you get the benefit of both साजपुर and White field growth areas.",
    },
    {
      time: 83,
      sender: "Aditya, lead",
      role: "lead",
      text: "Ok. Can you tell me a little bit more about the project?",
    },
    {
      time: 87,
      sender: "Priya (EchoLeads AI)",
      role: "ai",
      text: "Of course. Let me share...",
    },
    {
      time: 89,
      sender: "Aditya, lead",
      role: "lead",
      text: "Like, if amenities,",
    },
    {
      time: 91,
      sender: "Priya (EchoLeads AI)",
      role: "ai",
      text: "Ok. Let me share some key highlights and amenities for you. More than 85 percent open space with greenery, gardens, and wide roads. A 5 acre sports village including basketball, cycling, cricket nets, and running tracks. Horizon edge swimming pool and kids water play area. योगा pavilion and therapeutic walk. Beach vollyball",
    },
    {
      time: 111,
      sender: "Aditya, lead",
      role: "lead",
      text: "okay I want site visit.",
    },
    {
      time: 113,
      sender: "Priya (EchoLeads AI)",
      role: "ai",
      text: "That's wonderful. Right now, the prelaunch phase is going on. So you get the best pricing and inventory options. This weekend, we are arranging site visits. Shall I block a slot for you? Morning will be convenient or evening?",
    },
    {
      time: 128,
      sender: "Aditya, lead",
      role: "lead",
      text: "Evening.",
    },
    {
      time: 130,
      sender: "Priya (EchoLeads AI)",
      role: "ai",
      text: "Ok. I have blocked an evening slot for your site visit this weekend. Do you have any questions or queries regarding the project?",
    },
    {
      time: 139,
      sender: "Aditya, lead",
      role: "lead",
      text: "No, that's all. Thank you.",
    }
  ];

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      setTimerSeconds(Math.floor(current));
      
      let visible = 0;
      for (let i = 0; i < messages.length; i++) {
        if (current >= messages[i].time) {
          visible = i + 1;
        }
      }
      if (visible !== visibleMessages) {
        setVisibleMessages(visible);
      }
    }
  };

  const handleReplay = () => {
    setIsReplaying(true);
    setVisibleMessages(0);
    setTimerSeconds(0);

    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log(e));
    }
  };

  return (
    <section className="relative bg-white text-slate-900 pt-20 pb-24 lg:pt-28 lg:pb-32 overflow-hidden border-b border-slate-200/80">
      <div className="w-full max-w-[1200px] mx-auto px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
          {/* Left Column: Copy */}
          <div className="lg:col-span-6 space-y-7">
            <h1 className="text-5xl sm:text-6xl lg:text-[72px] font-extrabold text-slate-900 tracking-tight leading-[1.08]">
              Never miss a<br />
              lead again.
            </h1>

            <p className="text-lg sm:text-xl text-slate-600 max-w-xl leading-relaxed">
              EchoLeads is an AI voice agent that answers inbound calls, calls
              new leads in under 30 seconds, qualifies them and books demos, day
              and night. Your team only talks to buyers who are ready.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-5 pt-1">
              <a
                href="https://echoleads.ai/contact"
                className="inline-block px-8 py-3.5 rounded-lg bg-[#F2A339] hover:bg-[#E08D26] text-slate-950 font-bold text-base transition-all transform hover:-translate-y-0.5 shadow-lg shadow-[#F2A339]/25 cursor-pointer"
              >
                Book a demo
              </a>

              <a
                href="#how-it-works"
                className="text-slate-700 hover:text-[#F2A339] font-medium text-base underline underline-offset-4 transition-colors"
              >
                See how it works
              </a>
            </div>

            {/* Sub-trust text */}
            <div className="text-sm text-slate-500">
              Trusted by 100+ growing businesses. Backed by Google for Startups at T-Hub.
            </div>
          </div>

          {/* Right Column: Live Call Widget */}
          <div className="lg:col-span-6">
            <div className="bg-white border border-slate-200/90 rounded-2xl p-6 sm:p-7 shadow-xl space-y-5">
              <audio 
                ref={audioRef} 
                src="/real-estate-call.wav" 
                preload="auto" 
                onTimeUpdate={handleTimeUpdate}
                onEnded={() => setIsReplaying(false)}
              />
              {/* Header Bar */}
              <div className="flex items-center justify-between pb-3 border-b border-dashed border-slate-200">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                  </span>
                  <span className="text-xs font-semibold text-slate-900 tracking-wider">
                    Live call
                  </span>
                </div>

                <div className="text-xs font-mono text-slate-500">
                  00:{timerSeconds < 10 ? `0${timerSeconds}` : timerSeconds}
                </div>
              </div>

              {/* Waveform bars */}
              <div className="flex items-center gap-[3px] py-2 h-10 overflow-hidden">
                {waveformHeights.map((height, i) => (
                  <span
                    key={i}
                    className={`w-[3.5px] rounded-full bg-emerald-400 origin-center ${
                      isReplaying ? "[animation:wave-bounce_1.2s_ease-in-out_infinite]" : ""
                    }`}
                    style={{
                      height: `${height}px`,
                      animationDelay: `${i * 0.05}s`
                    }}
                  />
                ))}
              </div>

              {/* Status */}
              <div className="text-sm font-medium text-slate-500">
                {!isReplaying
                  ? "Call complete"
                  : visibleMessages > 0 && messages[visibleMessages - 1].role === "lead"
                  ? "Lead is speaking"
                  : "EchoLeads AI is speaking"}
              </div>

              {/* Chat Dialogue */}
              <div ref={chatRef} className="space-y-4 h-[220px] overflow-y-auto pr-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {messages.slice(0, visibleMessages).map((msg, index) => {
                  const isLastMessage = index === visibleMessages - 1;
                  const shouldType = isReplaying && isLastMessage;

                  return (
                    <div
                      key={index}
                      className={`space-y-1 animate-fadeIn ${
                        msg.role === "lead" ? "flex flex-col items-end" : "flex flex-col items-start"
                      }`}
                    >
                      <div className="text-[11px] font-medium text-slate-500 px-1">
                        {msg.sender}
                      </div>
                      <div
                        className={`px-4 py-3 rounded-xl text-sm leading-snug max-w-[88%] ${
                          msg.role === "lead"
                            ? "bg-slate-800 text-white rounded-tr-none"
                            : "bg-white border border-slate-200 text-slate-900 rounded-tl-none"
                        }`}
                      >
                        {shouldType ? <Typewriter text={msg.text} speed={40} /> : msg.text}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 2x2 Extracted Lead Data Grid */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg">
                  <div className="text-[11px] text-slate-500 mb-1">
                    Purpose
                  </div>
                  <div className="text-sm font-semibold text-slate-900">
                    Investment
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg">
                  <div className="text-[11px] text-slate-500 mb-1">
                    Budget
                  </div>
                  <div className="text-sm font-semibold text-slate-900">
                    1.2 to 1.5 CR
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg">
                  <div className="text-[11px] text-slate-500 mb-1">
                    Property
                  </div>
                  <div className="text-sm font-semibold text-slate-900">
                    3 BHK (Codename Saja Field)
                  </div>
                </div>

                <div className="bg-[#FFF9F0] border border-[#F2A339]/50 p-3 rounded-lg">
                  <div className="text-[11px] text-[#F2A339] mb-1">
                    Next step
                  </div>
                  <div className="text-sm font-semibold text-slate-900">
                    Site visit blocked (Evening)
                  </div>
                </div>
              </div>

              {/* Bottom Footer */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-200 text-xs">
                <span className="text-slate-500">Sample call for illustration</span>
                <button
                  onClick={handleReplay}
                  disabled={isReplaying}
                  className="flex items-center gap-1.5 px-3 py-1 rounded bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 hover:text-slate-900 transition-colors"
                >
                  <RotateCcw className="w-3 h-3" />
                  <span>Replay</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
